import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun, Globe2, ChevronDown } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_LINKS = [
  { href: "#services", label: "Services" },
  { href: "#destinations", label: "Destinations" },
  { href: "#process", label: "Process" },
  { href: "/blog", label: "Blog", route: true },
  { href: "#contact", label: "Contact" },
];

const COURSE_LINKS = [
  { to: "/courses/ielts", label: "IELTS Coaching" },
  { to: "/courses/pte", label: "PTE Academic" },
  { to: "/courses/spoken-english", label: "Spoken English" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    if (href.startsWith("#") && location.pathname === "/") {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0F3D8C] to-[#2563EB] flex items-center justify-center shadow-lg group-hover:shadow-[#D4AF37]/40 transition-shadow">
            <Globe2 className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-[15px] tracking-tight">Globe Vista</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Overseas Edu.</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.slice(0, 2).map((l) => (
            <a
              key={l.href}
              href={location.pathname === "/" ? l.href : `/${l.href}`}
              onClick={(e) => {
                if (location.pathname === "/") {
                  e.preventDefault();
                  handleNav(l.href);
                }
              }}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium hover:text-[#0F3D8C] dark:hover:text-[#FCD34D] transition-colors"
            >
              {l.label}
            </a>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-link-courses"
                className="inline-flex items-center gap-1 text-sm font-medium hover:text-[#0F3D8C] dark:hover:text-[#FCD34D] transition-colors outline-none"
              >
                Courses <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[220px] rounded-2xl mt-2">
              {COURSE_LINKS.map((c) => (
                <DropdownMenuItem key={c.to} asChild>
                  <Link to={c.to} data-testid={`nav-course-${c.to.split("/").pop()}`} className="cursor-pointer">
                    {c.label}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild>
                <a
                  href={location.pathname === "/" ? "#test-prep" : "/#test-prep"}
                  onClick={(e) => {
                    if (location.pathname === "/") {
                      e.preventDefault();
                      handleNav("#test-prep");
                    }
                  }}
                  className="cursor-pointer text-muted-foreground"
                  data-testid="nav-course-all"
                >
                  View all tests →
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {NAV_LINKS.slice(2).map((l) =>
            l.route ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium hover:text-[#0F3D8C] dark:hover:text-[#FCD34D] transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={location.pathname === "/" ? l.href : `/${l.href}`}
                onClick={(e) => {
                  if (location.pathname === "/") {
                    e.preventDefault();
                    handleNav(l.href);
                  }
                }}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium hover:text-[#0F3D8C] dark:hover:text-[#FCD34D] transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            className="p-2.5 rounded-full glass hover:scale-105 transition-transform"
          >
            {theme === "dark" ? <Sun className="w-4 h-4 text-[#FCD34D]" /> : <Moon className="w-4 h-4 text-[#0F3D8C]" />}
          </button>

          <Button
            data-testid="navbar-cta"
            onClick={() => handleNav("#contact")}
            className="hidden md:inline-flex rounded-full bg-[#0F3D8C] hover:bg-[#0C3275] dark:bg-[#2563EB] dark:hover:bg-[#3B82F6] text-white px-6"
          >
            Book Consultation
          </Button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-full glass"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-4 rounded-2xl glass-solid p-6 space-y-4">
          {NAV_LINKS.map((l) =>
            l.route ? (
              <Link
                key={l.href}
                to={l.href}
                onClick={() => setOpen(false)}
                className="block text-sm font-medium"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav(l.href);
                }}
                className="block text-sm font-medium"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            )
          )}
          <div className="pt-3 border-t border-border">
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">Courses</div>
            {COURSE_LINKS.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                className="block text-sm font-medium py-1.5"
                data-testid={`mobile-course-${c.to.split("/").pop()}`}
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
